import { createSlice } from '@reduxjs/toolkit';
import { fetchQueries } from './actions';
import { changeComplaintStatus } from '../changestatus/actions';

const initialState = {
  list: [],
  meta: null,
  isLoading: false,
  error: null,
  hasLoaded: false,
  requestedComplaintTypeId: null,
};

const unwrapQueries = (payload) => {
  const body = payload?.data ?? payload;
  if (Array.isArray(body)) {
    return { list: body, meta: payload?.meta ?? payload?.pagination ?? null };
  }
  const list = body?.data ?? body?.results ?? body?.items ?? [];
  return {
    list: Array.isArray(list) ? list : [],
    meta: body?.meta ?? body?.pagination ?? payload?.meta ?? payload?.pagination ?? null,
  };
};

const errorMessage = (action) =>
  action.payload?.message ?? action.error?.message ?? 'Failed to fetch queries';

const complaintIdOf = (item) => item?.complaintId ?? item?.id;

const applyStatusPatch = (list, action) => {
  const arg = action.meta?.arg ?? {};
  const updated = action.payload?.data ?? action.payload;
  const id = arg.id ?? complaintIdOf(updated);
  if (id == null) return list;

  return list.map((item) => {
    if (String(complaintIdOf(item)) !== String(id)) return item;

    const next = { ...item };
    if (updated && typeof updated === 'object' && !Array.isArray(updated)) {
      [
        'status',
        'statusId',
        'level',
        'levelNo',
        'currentStepId',
        'currentWorkflowStepId',
        'workflowStepId',
        'assignedToUserId',
      ].forEach((key) => {
        if (updated[key] !== undefined) next[key] = updated[key];
      });
    }
    if (arg.statusId != null) next.statusId = arg.statusId;
    if (arg.status) next.status = arg.status;
    if (arg.currentStepId != null) {
      next.currentStepId = arg.currentStepId;
      next.currentWorkflowStepId = arg.currentStepId;
      next.workflowStepId = arg.currentStepId;
    }
    if (arg.level != null) {
      next.level = arg.level;
      next.levelNo = arg.level;
    }
    if (arg.assignedToUserId != null) next.assignedToUserId = arg.assignedToUserId;
    return next;
  });
};

const complaintManagementSlice = createSlice({
  name: 'complaintManagement',
  initialState,
  reducers: {
    // Unused: nothing dispatches this helper.
    // clearComplaintManagementError: (state) => {
    //   state.error = null;
    // },
    resetComplaintManagementState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQueries.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.hasLoaded = false;
        state.list = [];
        state.requestedComplaintTypeId = action.meta.arg?.complaintTypeId ?? null;
      })
      .addCase(fetchQueries.fulfilled, (state, action) => {
        const requestedId = action.meta.arg?.complaintTypeId;
        if (
          state.requestedComplaintTypeId != null &&
          requestedId != null &&
          String(state.requestedComplaintTypeId) !== String(requestedId)
        ) {
          return;
        }
        const result = unwrapQueries(action.payload);
        state.isLoading = false;
        state.hasLoaded = true;
        state.list = result.list;
        state.meta = result.meta;
      })
      .addCase(fetchQueries.rejected, (state, action) => {
        if (action.meta.aborted) return;
        const requestedId = action.meta.arg?.complaintTypeId;
        if (
          state.requestedComplaintTypeId != null &&
          requestedId != null &&
          String(state.requestedComplaintTypeId) !== String(requestedId)
        ) {
          return;
        }
        state.isLoading = false;
        state.hasLoaded = true;
        state.error = errorMessage(action);
      })
      .addCase(changeComplaintStatus.fulfilled, (state, action) => {
        state.list = applyStatusPatch(state.list, action);
      });
  },
});

export const {
  // clearComplaintManagementError,
  resetComplaintManagementState,
} =
  complaintManagementSlice.actions;
export default complaintManagementSlice.reducer;
